/**
 * The WhatsApp draft: the shape the composer edits, and the two directions it
 * converts in — a stored template into a draft, and a draft into the document
 * `@wend/shared` reasons about.
 *
 * **It was inside `WaComposer.tsx` until slice 9, and it was moved for one
 * reason: it is the only part of that screen a test can reach.** `WaComposer`
 * imports the API client, which imports `lib/config.ts`, which reads
 * `import.meta.env` — so it evaluates under Vite and nowhere else, and a verify
 * script that imports it dies on the first line. That was tolerable while the
 * conversion was three ternaries over text fields. It stopped being tolerable
 * when {@link applyStoredFile} arrived, because that function decides whether
 * the document Meta receives points at bytes Wend actually has, and a function
 * with that job whose only coverage is "the screen compiles" is a function
 * nobody has checked. `verify-wa-header-media.tsx` calls it directly.
 *
 * The rejected alternative was exporting `applyStoredFile` from the composer and
 * leaving it there. It exports fine and imports fine; it simply cannot be run
 * outside a browser, which is the whole problem.
 *
 * **Nothing here fetches, and nothing here may.** The moment this file imports a
 * client it becomes as untestable as the screen it came from. It depends on
 * `@wend/shared` and on nothing else in the app.
 */
import {
  waMediaHeaderForReview,
  waTemplateDocumentSchema,
  type Template,
  type WaButton,
  type WaHeader,
  type WaHeaderFormat,
  type WaHeaderMediaAsset,
  type WaTemplateCategory,
  type WaTemplateDocument,
} from '@wend/shared';

/**
 * The document as the composer holds it.
 *
 * `header` is split into a format and the two possible payloads rather than kept
 * as the discriminated union, because a person switching text → image and back
 * should not lose what they typed. It is folded back into the union — or into
 * `null` — on the way out.
 *
 * **`headerHandle` and `headerFileName` are no longer typed by anybody.** They
 * were free-text fields until slice 9; they are now *derived* from the stored
 * file by {@link applyStoredFile}, at the two moments they can change — a file
 * is uploaded or removed, or the header format moves. They stay on the draft
 * rather than being computed at save time because the document Wend saves has to
 * be the document the person looked at, and because a template composed before
 * slice 9 can carry a handle with no file behind it: that value has to survive
 * being read, or opening such a template and saving it would silently strip the
 * only thing making it submittable.
 */
export interface WaDraft {
  name: string;
  category: WaTemplateCategory;
  language: string;
  headerFormat: WaHeaderFormat | 'none';
  headerText: string;
  headerHandle: string;
  headerFileName: string;
  body: string;
  footer: string;
  buttons: WaButton[];
}

export function waDraftOf(template: Template): WaDraft {
  // `whatsapp` is null on an email template, and 0012 refuses a row that is half
  // of each — so a null here is an email template routed to the wrong screen.
  const wa = template.whatsapp;
  if (wa === null) {
    throw new Error('This is not a WhatsApp template, so it has no WhatsApp document to edit.');
  }
  const header = wa.header;
  return {
    name: template.name,
    category: wa.category,
    language: wa.language,
    headerFormat: header === null ? 'none' : header.format,
    headerText: header !== null && header.format === 'text' ? header.text : '',
    headerHandle: header !== null && header.format !== 'text' ? header.mediaHandle : '',
    headerFileName: header !== null && header.format !== 'text' ? (header.fileName ?? '') : '',
    body: wa.body,
    footer: wa.footer ?? '',
    buttons: [...wa.buttons],
  };
}

/** The draft as the contract wants it. Not yet parsed — the caller validates. */
export function waDocumentOf(draft: WaDraft): unknown {
  const header: WaHeader | null =
    draft.headerFormat === 'none'
      ? null
      : draft.headerFormat === 'text'
        ? { format: 'text', text: draft.headerText }
        : {
            format: draft.headerFormat,
            mediaHandle: draft.headerHandle,
            // Omitted rather than sent empty: the contract's `fileName` is
            // optional, and '' is not a file name.
            ...(draft.headerFileName.trim() === '' ? {} : { fileName: draft.headerFileName }),
          };

  return {
    category: draft.category,
    language: draft.language,
    header,
    body: draft.body,
    footer: draft.footer.trim() === '' ? null : draft.footer,
    buttons: draft.buttons,
  };
}

/**
 * Rewrites the draft's media fields from the file the template actually holds.
 *
 * **`waMediaHeaderForReview` is the only thing that fills `mediaHandle` here**,
 * and it takes a `WaReviewHandle` — so a send-time media id cannot reach the
 * document's header even by mistake. That is the whole point of the two
 * non-interchangeable reference types in `@wend/shared`, and a composer that
 * copied `asset.reviewHandle.handle` out by hand would have given it up for one
 * saved line.
 *
 * The four answers, and why the first is not "clear it":
 *
 *   - **no stored file** → the draft is left exactly as it is. A template
 *     composed before slice 9 carries a hand-entered handle with nothing behind
 *     it, and clearing that on a format change would delete the only thing
 *     making it submittable. `WaHeaderMediaPanel` says loudly that such a
 *     template cannot be resubmitted; it does not need this function to punish
 *     it as well;
 *   - **stored file, but not for this slot** → cleared, because a handle is
 *     minted for *these* bytes. Left in place, Meta would review an image and
 *     approve a document header;
 *   - **stored file, no handle minted yet** → the name is kept and the handle
 *     stays empty, so the save is refused on `header.mediaHandle` rather than
 *     succeeding into a template that cannot be submitted. `null` there means
 *     "not minted yet", not "the upload failed";
 *   - **stored file with a handle** → both fields come from the constructor.
 */
export function applyStoredFile(
  draft: WaDraft,
  format: WaHeaderFormat | 'none',
  asset: WaHeaderMediaAsset | null,
): WaDraft {
  if (asset === null) return draft;

  if (format === 'none' || format === 'text' || asset.format !== format) {
    return { ...draft, headerHandle: '', headerFileName: '' };
  }

  if (asset.reviewHandle === null) {
    return { ...draft, headerHandle: '', headerFileName: asset.filename };
  }

  const header = waMediaHeaderForReview(asset, asset.reviewHandle);
  return { ...draft, headerHandle: header.mediaHandle, headerFileName: header.fileName ?? '' };
}

/**
 * The document, when it is complete enough to be one.
 *
 * Used for the live issue list and the preview, both of which want the shape
 * `@wend/shared` reasons about rather than the half-typed draft. A draft that
 * does not parse yet — an empty body on a new template — simply has no issues to
 * report and no preview to draw, which is the correct answer for it.
 */
export function parsedWaDocument(draft: WaDraft): WaTemplateDocument | null {
  const parsed = waTemplateDocumentSchema.safeParse(waDocumentOf(draft));
  return parsed.success ? parsed.data : null;
}

export function waDraftSignature(draft: WaDraft): string {
  return JSON.stringify(draft);
}
